/*
 * This file will be generated by template
 */
import "../css/my_main.css";
import { initAnalytics } from "./analytics";
import "./bible_group";
import { notfoundLogic } from "../not-found";
import { mainBible } from "./bible_group";
import { floatingUpButton } from "./floating_button";
import { fox_doctrine_table } from "./fox_doctrine_table";
import { ensureHashesIndexCache } from "./hashes_index_cache";
import { headerNav } from "./main_nav";
import {
	applyMenuConfigToDom,
	applyThemeToDom,
	buildLanguageRedirectPath,
	readMenuConfig,
	type ThemeMode,
	updateMenuConfig,
} from "./menu_config";
import {
	createMenuPopupController,
	type MenuPopupController,
} from "./menu_popup";
import {
	attachClickHandlerForSearch,
	trapScrollableNavbar,
} from "./navbar_drawer";
// import { installOriginalParagraphToggle } from "./original_paragraph_toggle";
import { initPagefindSearch } from "./pagefind_search";
import {
	checkIsMobile,
	RESIZE_CUSTOM,
	resizeDetection,
} from "./resize-detection";
import { responsiveTable } from "./set-responsive-table";
// Extracted modules
import { installWasmInstantiateStreamingFallback } from "./wasm_instantiate_fallback";

const time = Date.now();
// @ts-expect-error ignore
window.VERSION_TIME = time;

// Must run early so Pagefind works in VSCode Live Server
installWasmInstantiateStreamingFallback();

// Initialize Google Analytics ASAP so the pageview fires on every page load
// (core pages and R2-served content alike — my_main.js is injected everywhere).
initAnalytics();

/**
 * Nav scroll-lock (prevents body/html scroll while the drawer is open).
 * This ensures the drawer's `.scrollable` container receives scroll, especially on iOS Safari.
 */

function openNav(navId = "navBar"): void {
	const nav = document.getElementById(navId);
	// Do NOT force display via inline styles; it overrides CSS `display:flex` needed for `.scrollable` to scroll.
	nav?.style.removeProperty("display");
	nav?.style.setProperty("transform", "translate(0,0)");
}

function closeNav(navId = "navBar"): void {
	const nav = document.getElementById(navId);
	nav?.style.removeProperty("display");
	nav?.style.setProperty("transform", "translate(-100vw,0)");
}

const maybeRedirectToPreferredLanguage = (): void => {
	const existing = readMenuConfig();

	const targetPath = buildLanguageRedirectPath(
		window.location.pathname,
		existing.language,
	);
	if (!targetPath) return;

	const current = `${window.location.pathname}`;
	if (targetPath === current) return;

	window.location.replace(
		`${targetPath}${window.location.search}${window.location.hash}`,
	);
};

let currentTheme: ThemeMode = "light";
let menuButton: HTMLButtonElement | null = null;

const MENU_BUTTON_ROLE = "menu";

let menuPopupController: MenuPopupController | null = null;

// ==================================================
// Legacy table bgcolor sync (remove in dark mode, restore in light mode)
//
// Some legacy HTML contains: <table bgcolor="#FFFFCC"> ... </table>
// (sometimes also without the leading '#': bgcolor="FFFFCC").
//
// We remove that attribute in dark mode (so CSS/theme can control backgrounds),
// and restore it in light mode to preserve original look.
// ==================================================

const LEGACY_TABLE_BGCOLOR = "#FFFFCC";
const LEGACY_TABLE_BGCOLOR_NORMALIZED = "ffffcc";

const normalizeLegacyBgcolor = (value: string) =>
	value.trim().toLowerCase().replace(/^#/, "");

const isLegacyBgcolorMatch = (value: string | null | undefined) => {
	if (!value) return false;
	return normalizeLegacyBgcolor(value) === LEGACY_TABLE_BGCOLOR_NORMALIZED;
};

const legacyBgcolorTables = new Set<HTMLTableElement>();
const legacyBgcolorOriginal = new WeakMap<HTMLTableElement, string>();

let legacyBgcolorObserver: MutationObserver | null = null;
let legacyBgcolorSyncInitialized = false;
let isSyncingLegacyBgcolor = false;

const LEGACY_BGCOLOR_OBSERVER_OPTIONS: MutationObserverInit = {
	subtree: true,
	childList: true,
	attributes: true,
	attributeFilter: ["bgcolor"],
};

const maybeRegisterLegacyBgcolorTable = (el: Element) => {
	if (!(el instanceof HTMLTableElement)) return;

	const bgcolor = el.getAttribute("bgcolor");
	if (!isLegacyBgcolorMatch(bgcolor)) return;

	if (!legacyBgcolorOriginal.has(el)) {
		// Store exactly what the legacy HTML had (keeps case/#-prefix as-is).
		legacyBgcolorOriginal.set(el, bgcolor ?? LEGACY_TABLE_BGCOLOR);
	}
	legacyBgcolorTables.add(el);
};

const registerLegacyBgcolorTables = (root: Node | ParentNode = document) => {
	if (root instanceof Document || root instanceof DocumentFragment) {
		const candidates = Array.from(
			root.querySelectorAll("table[bgcolor]"),
		) as HTMLTableElement[];
		for (const table of candidates) maybeRegisterLegacyBgcolorTable(table);
		return;
	}

	if (root instanceof Element) {
		// Include the element itself if it's a table.
		maybeRegisterLegacyBgcolorTable(root);

		const candidates = Array.from(
			root.querySelectorAll("table[bgcolor]"),
		) as HTMLTableElement[];
		for (const table of candidates) maybeRegisterLegacyBgcolorTable(table);
	}
};

const pauseLegacyBgcolorObserver = () => {
	if (!legacyBgcolorObserver) return () => {};
	legacyBgcolorObserver.disconnect();
	return () => {
		legacyBgcolorObserver?.observe(
			document.documentElement,
			LEGACY_BGCOLOR_OBSERVER_OPTIONS,
		);
	};
};

const syncLegacyBgcolorTablesForTheme = (theme: ThemeMode) => {
	if (isSyncingLegacyBgcolor) return;
	isSyncingLegacyBgcolor = true;

	const resumeObserver = pauseLegacyBgcolorObserver();

	try {
		// Discover any legacy-bgcolor tables still present in the DOM.
		registerLegacyBgcolorTables(document);

		// Drop dead references.
		for (const table of Array.from(legacyBgcolorTables)) {
			if (!table.isConnected) legacyBgcolorTables.delete(table);
		}

		const isDark = theme === "dark";

		for (const table of legacyBgcolorTables) {
			const original = legacyBgcolorOriginal.get(table) ?? LEGACY_TABLE_BGCOLOR;
			const current = table.getAttribute("bgcolor");

			if (isDark) {
				// Only remove when the attribute is the legacy color (don't interfere with other colors).
				if (isLegacyBgcolorMatch(current)) table.removeAttribute("bgcolor");
			} else {
				// Restore only if missing (don't override other colors someone may have set).
				if (!current) table.setAttribute("bgcolor", original);
			}
		}
	} finally {
		resumeObserver();
		isSyncingLegacyBgcolor = false;
	}
};

const initLegacyBgcolorSync = () => {
	if (legacyBgcolorSyncInitialized) return;
	legacyBgcolorSyncInitialized = true;

	// Initial pass.
	syncLegacyBgcolorTablesForTheme(currentTheme);

	// Keep in sync for pages/scripts that add tables after load (or reintroduce bgcolor).
	legacyBgcolorObserver = new MutationObserver((mutations) => {
		let needsSync = false;

		for (const m of mutations) {
			if (m.type === "attributes") {
				const el = m.target;
				if (el instanceof Element && m.attributeName === "bgcolor") {
					maybeRegisterLegacyBgcolorTable(el);
					needsSync = true;
				}
				continue;
			}

			if (m.type === "childList") {
				for (const node of Array.from(m.addedNodes)) {
					registerLegacyBgcolorTables(node);
				}
				needsSync = true;
			}
		}

		if (needsSync) syncLegacyBgcolorTablesForTheme(currentTheme);
	});

	legacyBgcolorObserver.observe(
		document.documentElement,
		LEGACY_BGCOLOR_OBSERVER_OPTIONS,
	);
};

const applyTheme = (theme: ThemeMode) => {
	currentTheme = theme;

	// Persist + apply via shared MenuConfig (canonical localStorage state).
	const existing = readMenuConfig();
	const next =
		existing.theme === theme ? existing : updateMenuConfig({ theme });

	applyThemeToDom(theme);
	applyMenuConfigToDom(next);

	// Keep legacy-bgcolor tables visually correct per theme.
	syncLegacyBgcolorTablesForTheme(theme);

	console.log("[my_main] applyTheme", {
		theme,
		readyState: document.readyState,
	});
};

/**
 * Floating menu button action:
 * toggles the anchored popup (instead of navigating to /menu.html).
 */
const onMenuButtonClick = () => menuPopupController?.toggle();

const ensureThemeToggleControls = (): {
	container: HTMLDivElement;
	menuButton: HTMLButtonElement;
} => {
	let container = document.querySelector(
		".theme-toggle",
	) as HTMLDivElement | null;
	if (!container) {
		container = document.createElement("div");
		container.className = "theme-toggle";
		document.body.append(container);
	}

	// Remove legacy theme toggle buttons; keep only the menu button.
	for (const el of Array.from(container.querySelectorAll("button"))) {
		const btn = el as HTMLButtonElement;
		if (btn.dataset.role !== MENU_BUTTON_ROLE) btn.remove();
	}

	let menuButton = container.querySelector(
		`button[data-role="${MENU_BUTTON_ROLE}"]`,
	) as HTMLButtonElement | null;

	if (!menuButton) {
		menuButton = document.createElement("button");
		menuButton.dataset.role = MENU_BUTTON_ROLE;
		container.appendChild(menuButton);
	}

	menuButton.type = "button";

	// Avoid duplicate event listeners if scripts load twice.
	menuButton.onclick = onMenuButtonClick;
	menuButton.setAttribute("aria-haspopup", "dialog");
	menuButton.setAttribute("aria-expanded", "false");
	menuButton.setAttribute("aria-controls", "hallv-menu-popup");

	menuButton.innerHTML = `
		<span class="theme-toggle__icon" aria-hidden="true">☰</span>
		<span class="theme-toggle__text">Menu</span>
	`;
	menuButton.setAttribute("aria-label", "Open menu");

	return { container, menuButton };
};

const initThemeToggle = () => {
	console.log("[my_main] initThemeToggle start");

	const { container, menuButton: ensuredMenuButton } =
		ensureThemeToggleControls();

	// Always show the floating Menu button (we no longer rely on the scroll/click
	// logic from [`floating_button.ts`](src/main_script/floating_button.ts:1) to reveal it).
	container.classList.add("show");

	menuButton = ensuredMenuButton;

	menuPopupController ??= createMenuPopupController({
		getMenuButton: () => menuButton,
		applyTheme,
	});

	console.log("[my_main] initThemeToggle ensured controls", {
		hasContainer: !!container,
	});
};

// Redirect to language-specific HTML (based on user-selected menu language)
maybeRedirectToPreferredLanguage();

// Apply theme ASAP to avoid flash
applyTheme(readMenuConfig().theme);

// Also start observing ASAP, so tables parsed/added later are handled (reduces "flash" in dark mode).
initLegacyBgcolorSync();

if ("serviceWorker" in navigator) {
	// host should be google.com with out protocol like http://
	const hostWithPort = window.location.host.includes(":");
	const filename = "/service-worker.js";
	const path = hostWithPort ? `/public${filename}` : filename;
	console.log("[my_main] serviceWorker path", path);

	// When an ALREADY-ACTIVE SW gets replaced by a new/updated one (it calls
	// skipWaiting + clients.claim), the already-rendered page was fetched under
	// the OLD controller, so its content (e.g. a stale 404 from an older SW)
	// won't fix itself. Reload once so the page re-fetches through the
	// now-controlling SW. Guarded to avoid reload loops.
	//
	// Only do this for a genuine update (there WAS a controller before this
	// page's own registration call). A visitor's very first-ever visit has no
	// prior controller -- the initial HTML was served straight from Firebase,
	// never through any SW, so there's no stale content to fix -- and forcing
	// a reload there just interrupts the page (and was cancelling the GA
	// pageview beacon before it could send, confirmed via network capture:
	// every first visit reloaded before any analytics request completed).
	const hadControllerBeforeRegister = !!navigator.serviceWorker.controller;
	let reloadedForSW = false;
	navigator.serviceWorker.addEventListener("controllerchange", () => {
		if (reloadedForSW || !hadControllerBeforeRegister) return;
		reloadedForSW = true;
		console.log("[my_main] SW controller changed (update) -> reloading once");
		window.location.reload();
	});

	// Register the SW so it can proxy content requests to R2 (see service-worker.js).
	navigator.serviceWorker.register(path, { scope: "/" }).catch((err) => {
		console.warn("[my_main] SW registration failed", err);
	});
}

resizeDetection();
if (document.readyState === "loading") {
	console.log("[my_main] document loading: wait DOMContentLoaded");
	document.addEventListener("DOMContentLoaded", onReady);
} else {
	console.log(
		"[my_main] document readyState:",
		document.readyState,
		"calling onReady immediately",
	);
	onReady();
}

async function onReady() {
	console.log("[my_main] onReady start");
	initThemeToggle();

	// Ensure MenuConfig is present and applied (font/lang).
	applyMenuConfigToDom(readMenuConfig());

	floatingUpButton();
	responsiveTable();
	// toggle message-from-lord
	toggleMessageFromLord();

	// Toggle "original" paragraph placeholders (runs after window `load`)
	// installOriginalParagraphToggle();

	if (document.body.classList.contains("not-found-page")) {
		console.log("[my_main] not-found page, stop");
		notfoundLogic();
		return;
	}
	if (window.location.pathname.includes("/commented_bible")) {
		console.log("[my_main] commented_bible path -> mainBible");
		mainBible();
		return;
	}

	// Ensure IndexedDB `hashes_index` is populated with the latest hashes (based on hashes_index.meta.json).
	void ensureHashesIndexCache().catch((err) => {
		console.warn("[my_main] hashes_index init failed", err);
	});

	fox_doctrine_table();
	headerNav();

	let iconClassName = "hamburger-menu";
	window.addEventListener(RESIZE_CUSTOM, onMainResize);
	onMainResize();

	function onMainResize() {
		const isMobile = checkIsMobile();
		if (isMobile) {
			iconClassName = "right-caret";
		}

		const navBar = document.querySelector("#navBar") as HTMLElement;
		if (!navBar) return;

		attachClickHandlerForSearch(navBar);
		trapScrollableNavbar(navBar);

		const hasScrollable = navBar.querySelector(".scrollable");
		if (hasScrollable) {
			// Important: some pages already have `.scrollable` in the HTML, so we still need to wire Pagefind.
			initPagefindSearch(navBar, {
				inputDebounceMs: 300,
				pagefindUiDebounceMs: 300,
			});
			return;
		}

		const navBarRelative = document.createElement("div") as HTMLDivElement;
		navBarRelative.classList.add("navbar-relative");

		const iconOpen = document.createElement("div");
		navBarRelative.appendChild(iconOpen);

		iconOpen.className = iconClassName;

		const navBarToggle = document.createElement("div") as HTMLDivElement;
		navBarToggle.className = "navbar-toggle";

		navBar.remove();
		const cloneNavbar = navBar.cloneNode(true) as HTMLDivElement;
		const closeBtn = document.createElement("div") as HTMLDivElement;
		closeBtn.classList.add("closeBtn");
		closeBtn.innerHTML = "&#10005;";
		cloneNavbar.style.transform = "translate(-100vw,0)";
		cloneNavbar.prepend(closeBtn);
		navBarToggle.append(navBarRelative);
		navBarRelative.append(cloneNavbar);
		document.body.append(navBarToggle);

		// change the structure
		const navBarScrollable = document.createElement("div");
		navBarScrollable.classList.add("scrollable");
		const contents = Array.from(
			document.querySelectorAll("#navBar .relatedLinks"),
		);
		contents.forEach((c) => {
			c.remove();
			navBarScrollable.append(c);
		});
		cloneNavbar.append(navBarScrollable);

		// ensure the scroll trap is applied to the actual scroll container
		trapScrollableNavbar(cloneNavbar);

		// hook Pagefind search into the drawer's search input
		initPagefindSearch(cloneNavbar, {
			inputDebounceMs: 300,
			pagefindUiDebounceMs: 300,
		});

		let isOpen = false;

		iconOpen.addEventListener("click", (e) => {
			e.preventDefault();
			e.stopPropagation();
			isOpen = true;
			openNav();
		});

		closeBtn.addEventListener("click", (e) => {
			e.preventDefault();
			e.stopPropagation();
			closeNav();
			isOpen = false;
		});

		document.addEventListener("click", (e) => {
			if (!isOpen) return;
			if (cloneNavbar.contains(e.target as Node)) return;
			closeNav();
			isOpen = false;
		});
	}
}

function toggleMessageFromLord() {
	const messageFromLord = document.querySelector(".message-from-lord");
	if (!messageFromLord) return;

	window.addEventListener(RESIZE_CUSTOM, onMessageFromLordResize);
	onMessageFromLordResize();
	function onMessageFromLordResize() {
		const isMobile = checkIsMobile();
		if (!isMobile) return;

		// prevent duplicate
		const accordionId = "accordion-toggle";
		if (document.getElementById(accordionId)) return;

		// add toggle
		const input = document.createElement("input");
		input.type = "checkbox";
		input.classList.add("accordion-toggle");
		input.id = accordionId;

		const label = document.createElement("label");
		const hideMessage = `Click To Show Message From Lord` + "&#9650";
		const showMessage = `Click To Hide Message From Lord` + "&#9660";

		label.classList.add(`accordion-label`);
		label.setAttribute("for", accordionId);
		messageFromLord?.parentElement?.insertBefore(input, messageFromLord);
		messageFromLord?.parentElement?.insertBefore(label, messageFromLord);

		let isShown = true;
		input.checked = isShown;
		const onToggle = () => {
			if (isShown) {
				label.innerHTML = showMessage;
				label.classList.add("show");
				isShown = false;
			} else {
				label.innerHTML = hideMessage;
				label.classList.remove("show");
				label.classList.add("hide");
				isShown = true;
			}
		};
		onToggle();
		label.addEventListener("click", onToggle);
	}
}

// ================================================ Text To Audio

(async () => {
	document.querySelector('td[valign="top"] table')?.remove();

	const PElements = Array.from(document.querySelectorAll('td[valign="top"] p'));
	const texts = PElements.map((p) => p.textContent).sort((a, b) =>
		(b?.length ?? 0) > (a?.length ?? 0) ? 1 : -1,
	);
	void texts;
})();
